import { $$ } from "@tento-ui/fastdom";
import { CalendarMonth } from "@tento-ui/components/calendar-month/calendar-month";
import { CalendarMonthGesture } from "@tento-ui/components/calendar-month/calendar-month-gesture";
import { AnimatedPanel } from "@tento-ui/components/animated-panel";
import type { ListItem } from "@tento-ui/components/entry-list/list-item";
import { type Option } from "@tento-lona/sheets";
import { Point } from "@tento-core/point";
import { NaiveDate, TimezoneRegion } from "@tento-chrono";
import { dateRangeToUtcTimeRange } from "@tento-lona/sheets-ui/plugins/layout/grid/month/calendar-month-utils.ts";
import { AllDaySpan } from "@tento-lona/sheets-ui/plugins/layout/grid/month/all-day-span.ts";
import { CalendarPreferences } from "@tento-lona";
import type { CalendarMonthCreateFormElement } from "./ui-delegate.ts";
import type { EventDetailsRevealContext } from "@tento-lona/sheets-ui/sheet/gestures/gesture-system.ts";
import { invariant } from "@tento-core/assert";
import { EventId, EventItem, EventItemAllDay, EventItemTimed } from "@tento-lona/sheets";
import type { MonthActionsContext } from "./month-actions-context.ts";
import type { MonthSpanBuilder } from "./month-span-builder.ts";

/** The popover a month grid has open: its kind, event and anchor. */
type ActivePopover = {
  type: "new" | "existing" | "existing-partial";
  event?: EventItemAllDay | EventItemTimed;
  dateRange: NaiveDate.Range;
  $activeElement: Option<HTMLElement>;
};

/**
 * The month view's popovers and menus: event details (through the unified
 * details manager, or directly), the create-event form and its pending
 * span, and the context menu, with the clicks and create gestures that open
 * them. Positions every popover against the grid's cells.
 */
export class MonthPopoverController {
  static readonly #POPOVER_ID = "calendar-month-event-details";
  static readonly #CONTEXT_MENU_ID = "calendar-month-context-menu";
  static readonly #PENDING_EVENT_STATE_ID = "calendar-month-pending-event";
  static readonly #EXISTING_EVENT_STATE_ID = "calendar-month-existing-event";
  static readonly #CONTEXT_MENU_STATE_ID = "calendar-month-context-menu-state";

  #$anchor: Option<AnimatedPanel> = null;
  #anchorId = 0;
  #activePopover: Option<ActivePopover> = null;

  readonly #ctx: MonthActionsContext;
  /** The span lane the create form repaints as its pending event changes. */
  readonly #spans: MonthSpanBuilder;

  constructor(ctx: MonthActionsContext, spans: MonthSpanBuilder) {
    this.#ctx = ctx;
    this.#spans = spans;
  }

  get #prefs(): CalendarPreferences {
    return this.#ctx.coordinator.eventDetailsManager.calendarPreferences;
  }

  isPopoverActive(): boolean {
    return this.#activePopover != null;
  }

  isContextMenuActive(): boolean {
    return this.#ctx.coordinator.globalState.hasState(MonthPopoverController.#CONTEXT_MENU_STATE_ID);
  }

  /** True when a pending (in-flight) new event exists. */
  hasPendingEvent(): boolean {
    return this.#ctx.delegate.getPendingEvent() != null;
  }

  #setPendingEventRange(dateRange: NaiveDate.Range): void {
    const current = this.#ctx.delegate.getPendingEvent();
    this.#ctx.delegate.setPendingEvent({
      dateRange,
      title: current?.title ?? null,
      category: current?.category ?? null,
      backgroundColor: current?.backgroundColor ?? null,
    });
  }

  #bindCreateFormLiveUpdates(
    $content: CalendarMonthCreateFormElement,
  ): void {
    const pendingEvent = this.#ctx.delegate.getPendingEvent();
    if (pendingEvent?.title != null) $content.eventTitle = pendingEvent.title;
    if (pendingEvent?.category != null) {
      $content.eventCategory = this.#ctx.sdkEvents.categories.itemOpt(pendingEvent.category);
    }
    $content.onUpdate = (next) => {
      const current = this.#ctx.delegate.getPendingEvent();
      if (!current) return;
      this.#ctx.delegate.setPendingEvent({
        ...current,
        title: next.title,
        category: next.category?.name ?? null,
        backgroundColor: next.backgroundColor,
      });
      this.#ctx.$calendar.updateSpans(this.#spans.buildAllSpans());
    };
  }

  /**
   * Set the pending-event range and trigger a re-bind so the span
   * paints. Used by the `reshowDetails` path in the new-event gesture
   * registration on the renderer side.
   */
  setPendingEventForReshow(dateRange: NaiveDate.Range): void {
    this.#setPendingEventRange(dateRange);
    this.#ctx.delegate.rebindCalendar();
  }

  async showNewEventPopover(dateRange: NaiveDate.Range): Promise<void> {
    const handle = this.#ctx.delegate.getHandle();
    const rowId = this.#ctx.delegate.getRowId();
    const tz = this.#ctx.delegate.getTz();
    const primaryCal = this.#ctx.calendars.peekPrimaryOrFirst();
    invariant(handle, "showNewEventPopover: layout handle not set");
    invariant(rowId, "showNewEventPopover: bound rowId not set");
    invariant(tz, "showNewEventPopover: timezone not set");
    invariant(
      primaryCal,
      "showNewEventPopover: no primary calendar configured",
    );

    const timeRange = dateRangeToUtcTimeRange(dateRange, tz);

    const row = this.#ctx.eventsSource.createTarget();
    invariant(row, "showNewEventPopover: no event-capable row under bound row", {
      boundRow: this.#ctx.delegate.getBoundRow()?.id.toString(),
    });
    const $content = this.#ctx.ui.createEventForm({
      calendars: this.#ctx.calendars,
      row,
      sdkEvents: this.#ctx.sdkEvents,
      calendarPreferences: this.#prefs,
      timeRange,
      tz,
      primaryCalendar: primaryCal,
      isAllDay: true,
      allDayDateRange: dateRange,
      onDismiss: this.hidePopover.bind(this),
    });
    this.#bindCreateFormLiveUpdates($content);
    $content.style.padding = "16px 16px";

    const offsetY = this.#computeSpanOffsetY(dateRange);

    this.#registerPopoverState(MonthPopoverController.#PENDING_EVENT_STATE_ID);
    this.#revealPopover($content, dateRange.start, offsetY, {
      type: "new",
      dateRange,
      $activeElement: null,
    });
    $$.defer(() => $content.focusTitle?.());
  }

  showExistingEventPopover(
    event: EventItemAllDay,
    clickedDate: Option<NaiveDate>,
    $activeElement: Option<HTMLElement>,
  ): void {
    const tz = this.#ctx.delegate.getTz();
    if (!tz) return;

    const eventRange = new NaiveDate.Range(event.time.start, event.time.end);
    const anchorDate = clickedDate ?? event.time.start;
    const offsetY = this.#computeSpanOffsetY(eventRange);

    this.#showEventWithUnifiedManager(event, tz, anchorDate, offsetY, {
      type: "existing",
      event,
      dateRange: eventRange,
      $activeElement,
    });
  }

  showExistingPartialDayEventPopover(
    event: EventItemTimed,
    cellDate: NaiveDate,
    calendarRelativeY: Option<number>,
    $activeElement: Option<HTMLElement>,
  ): void {
    const tz = this.#ctx.delegate.getTz();
    if (!tz) return;

    // If calendarRelativeY is provided, compute cell-relative offset by
    // subtracting the cell's position within the calendar.
    let offsetY: number;
    if (calendarRelativeY != null) {
      const $cell = this.#ctx.$calendar.getCellForDate(cellDate);
      if (!$cell) return;
      const calendarRect = this.#ctx.$calendar.getBoundingClientRect();
      const cellRect = $cell.getBoundingClientRect();
      offsetY = calendarRelativeY - (cellRect.top - calendarRect.top);
    } else {
      offsetY = this.#estimateEventListOffsetY(cellDate);
    }
    const dateRange = new NaiveDate.Range(cellDate, cellDate);

    this.#showEventWithUnifiedManager(event, tz, cellDate, offsetY, {
      type: "existing-partial",
      event,
      dateRange,
      $activeElement,
    });
  }

  /**
   * Show event details using the unified manager for cross-plugin coordination.
   */
  async #showEventWithUnifiedManager(
    event: EventItem,
    tz: TimezoneRegion,
    anchorDate: NaiveDate,
    offsetY: number,
    popoverInfo: ActivePopover,
  ): Promise<void> {
    const handle = this.#ctx.delegate.getHandle();
    const rowId = this.#ctx.delegate.getRowId();
    if (!handle || !rowId) return;

    const manager = this.#ctx.coordinator.eventDetailsManager;

    if (!manager) {
      // Fallback to direct display if manager not available
      const prefs = this.#prefs;
      const row = this.#ctx.eventsSource.ownerOf(event.id);
      if (!row) return;
      const $content = this.#ctx.ui.createEventDetails({
        event,
        tz,
        calendars: this.#ctx.calendars,
        row,
        timeFormat: prefs.peek().timeFormat,
        dateFormat: prefs.peek().dateFormat,
        sdkEvents: this.#ctx.sdkEvents,
        access: this.#ctx.calendarListAccess(),
      });
      $content.style.padding = "4px";
      this.#registerPopoverState(MonthPopoverController.#EXISTING_EVENT_STATE_ID);
      this.#revealPopover($content, anchorDate, offsetY, popoverInfo);
      this.#ctx.eventActiveState.activate(
        "details",
        event.id,
        popoverInfo.$activeElement,
      );
      return;
    }

    // Create reveal context for the unified manager
    const surface = handle.staticChild.surface(rowId);
    if (!surface) return;

    const activate = () => {
      this.#ctx.eventActiveState.activate(
        "details",
        event.id,
        popoverInfo.$activeElement,
      );
    };

    const context: EventDetailsRevealContext = {
      mode: "popover",
      surface,
      access: this.#ctx.calendarListAccess(),
      reveal: ($content: HTMLElement) => {
        this.#registerPopoverState(MonthPopoverController.#EXISTING_EVENT_STATE_ID);
        this.#revealPopover($content, anchorDate, offsetY, popoverInfo);
        activate();
      },
      hide: this.hidePopover.bind(this),
      update: ($content: HTMLElement) => {
        // In-place update: reposition and swap content without dismiss cycle
        this.#revealPopover($content, anchorDate, offsetY, popoverInfo);
        activate();
      },
    };

    manager.showEventDetails(event, tz, context);
  }

  hidePopover(): void {
    const activeEvent = this.#activePopover?.event ?? null;
    if (activeEvent != null) {
      this.#ctx.eventActiveState.clear("details", activeEvent.id);
    }
    if (!this.#$anchor) {
      this.#activePopover = null;
      return;
    }
    this.#ctx.coordinator.globalState.unregisterState(MonthPopoverController.#PENDING_EVENT_STATE_ID);
    this.#ctx.coordinator.globalState.unregisterState(MonthPopoverController.#EXISTING_EVENT_STATE_ID);

    const dismissingAnchorId = this.#anchorId;

    // Capture anchor locally and clear immediately to prevent race conditions
    // where a new reveal starts before the dismiss animation completes
    const anchorToDismiss = this.#$anchor;
    this.#$anchor = null;

    // Animate out, then clean up
    if (anchorToDismiss) {
      anchorToDismiss.dismiss().then(() => {
        // Only dismiss from staticChild if no new anchor was created
        // Otherwise we'd dismiss the new anchor by mistake
        if (this.#anchorId === dismissingAnchorId) {
          const handle = this.#ctx.delegate.getHandle();
          const rowId = this.#ctx.delegate.getRowId();
          if (handle && rowId) {
            handle.staticChild.dismiss(MonthPopoverController.#POPOVER_ID, rowId);
          }
        }
      });
    }

    // If we were creating a new event, clear the pending state
    if (this.#activePopover?.type === "new") {
      this.#ctx.delegate.setPendingEvent(null);
    }

    this.#activePopover = null;
    this.#ctx.delegate.rebindCalendar();
  }

  hidePopoverTemporarily(): void {
    const activeEvent = this.#activePopover?.event ?? null;
    if (activeEvent != null) {
      this.#ctx.eventActiveState.clear("details", activeEvent.id);
    }
    // Dismiss immediately without animation (for drag gestures)
    this.#$anchor?.dismissImmediate();
  }

  /** @param calendarOffset Position relative to the calendar element's top-left. */
  async showContextMenu(
    event: EventItemAllDay,
    calendarOffset: Point,
    $activeElement: Option<HTMLElement>,
  ): Promise<void> {
    // Dismiss event details (same system) and other systems' menus
    this.hidePopover();
    this.#ctx.coordinator.globalState.dismissOtherSystems(this.#ctx.system, null);
    this.dismissContextMenu();
    const access = this.#ctx.accessOf(event);
    if (!access.menu) return;

    const handle = this.#ctx.delegate.getHandle();
    const rowId = this.#ctx.delegate.getRowId();
    if (!handle || !rowId) return;

    const row = this.#ctx.eventsSource.ownerOf(event.id);
    if (!row) return;
    const $menu = this.#ctx.ui.createContextMenu({
      event,
      access,
      row,
      calendars: this.#ctx.calendars,
      onDismiss: this.dismissContextMenu.bind(this),
    });
    $menu.setAttribute("context", "calendar-month-event-context-menu");

    const x = calendarOffset.x;
    const y = calendarOffset.y;

    handle.staticChild.reveal(
      MonthPopoverController.#CONTEXT_MENU_ID,
      $menu,
      rowId,
      { offset: { x, y } },
    );

    this.#ctx.coordinator.notifyMenuOpened(this.#ctx.system);
    this.#ctx.coordinator.globalState.registerState({
      source: this.#ctx.system,
      id: MonthPopoverController.#CONTEXT_MENU_STATE_ID,
      blocking: false,
      dismiss: () => this.dismissContextMenu(),
    });
    this.#ctx.eventActiveState.activate("context-menu", event.id, $activeElement);
  }

  dismissContextMenu(): void {
    this.#ctx.eventActiveState.clear("context-menu", null);
    this.#ctx.coordinator.globalState.unregisterState(MonthPopoverController.#CONTEXT_MENU_STATE_ID);

    const handle = this.#ctx.delegate.getHandle();
    const rowId = this.#ctx.delegate.getRowId();
    if (handle && rowId) {
      handle.staticChild.dismiss(MonthPopoverController.#CONTEXT_MENU_ID, rowId);
    }
  }

  async reshowPopover(): Promise<void> {
    const pendingEvent = this.#ctx.delegate.getPendingEvent();
    const tz = this.#ctx.delegate.getTz();
    const primaryCal = this.#ctx.calendars.peekPrimaryOrFirst();
    if (!this.#activePopover || !pendingEvent || !tz || !primaryCal) return;

    const dateRange = pendingEvent.dateRange;
    const offsetY = this.#computeSpanOffsetY(dateRange);
    const timeRange = dateRangeToUtcTimeRange(dateRange, tz);

    const row = this.#ctx.eventsSource.createTarget();
    if (!row) return;
    const $content = this.#ctx.ui.createEventForm({
      calendars: this.#ctx.calendars,
      row,
      sdkEvents: this.#ctx.sdkEvents,
      calendarPreferences: this.#prefs,
      timeRange,
      tz,
      primaryCalendar: primaryCal,
      isAllDay: true,
      allDayDateRange: dateRange,
      onDismiss: this.hidePopover.bind(this),
    });
    this.#bindCreateFormLiveUpdates($content);
    $content.style.padding = "16px 16px";

    this.#revealPopover($content, dateRange.start, offsetY, this.#activePopover);
  }

  #registerPopoverState(stateId: string): void {
    this.#ctx.coordinator.globalState.dismissOtherSystems(this.#ctx.system, null);
    this.#ctx.coordinator.globalState.registerState({
      source: this.#ctx.system,
      id: stateId,
      blocking: false,
      dismiss: () => this.hidePopover(),
    });
  }

  #revealPopover(
    $content: HTMLElement,
    date: NaiveDate,
    offsetY: number,
    popoverInfo: ActivePopover,
  ): void {
    // Dismiss context menu if open (same-system coordination)
    this.dismissContextMenu();

    const handle = this.#ctx.delegate.getHandle();
    const rowId = this.#ctx.delegate.getRowId();
    if (!handle || !rowId) return;

    // Get the cell position relative to the calendar (0,0 = calendar top-left)
    const $cell = this.#ctx.$calendar.getCellForDate(date);
    if (!$cell) return;

    const calendarRect = this.#ctx.$calendar.getBoundingClientRect();
    const cellRect = $cell.getBoundingClientRect();

    // Position relative to calendar's top-left
    // Add 12px to account for calendar-month's y-offset within the sheet row
    const popoverWidth = 320;
    const padding = 8;
    const visibleWidth = handle.state.visibleWidthPx ?? calendarRect.width;
    let x = cellRect.left - calendarRect.left;

    // Clamp x to keep popover within visible area with padding
    const minX = padding;
    const maxX = visibleWidth - popoverWidth - padding;
    x = Math.max(minX, Math.min(x, maxX));

    const y = cellRect.top - calendarRect.top + offsetY + 4;

    // If anchor exists and is revealed, update content and position smoothly
    if (this.#$anchor?.getIsRevealed()) {
      // If switching from "new" to existing event, clear pending state
      if (this.#activePopover?.type === "new" && popoverInfo.type !== "new") {
        this.#ctx.delegate.setPendingEvent(null);
        this.#ctx.delegate.rebindCalendar();
      }

      // Re-reveal at new position (staticChild.reveal handles repositioning)
      handle.staticChild.updateOffset(
        MonthPopoverController.#POPOVER_ID,
        rowId,
        { x, y },
        "instant",
      );
      this.#$anchor.updateContent($content);
      this.#activePopover = popoverInfo;
      return;
    }

    // Create new anchor and do full reveal
    if (!this.#$anchor) {
      this.#anchorId++;
      this.#$anchor = AnimatedPanel.make();
    }

    // Position anchor via staticChild and reveal with animation
    handle.staticChild.reveal(
      MonthPopoverController.#POPOVER_ID,
      this.#$anchor,
      rowId,
      { offset: { x, y } },
    );
    this.#$anchor.reveal($content);
    this.#activePopover = popoverInfo;
  }

  /**
   * Compute the Y offset for positioning the popover below the span.
   * Returns offset relative to the cell's top, not the calendar's top.
   */
  #computeSpanOffsetY(dateRange: NaiveDate.Range): number {
    // getSpanBottomForDate returns position relative to calendar
    const spanBottom = this.#ctx.$calendar.getSpanBottomForDate(
      dateRange.start,
      dateRange,
    );

    if (spanBottom == null) {
      // Default offset (day number height + gap)
      return 52;
    }

    // Convert from calendar-relative to cell-relative
    const $cell = this.#ctx.$calendar.getCellForDate(dateRange.start);
    if (!$cell) return 52;

    const calendarRect = this.#ctx.$calendar.getBoundingClientRect();
    const cellRect = $cell.getBoundingClientRect();
    const cellTopRelativeToCalendar = cellRect.top - calendarRect.top;

    return spanBottom - cellTopRelativeToCalendar;
  }

  /** Estimate Y offset for event list items (below spans + day number). */
  #estimateEventListOffsetY(date: NaiveDate): number {
    const $cell = this.#ctx.$calendar.getCellForDate(date);
    if (!$cell) return 52;
    return this.#ctx.$calendar.getDayNumberRowHeight($cell) + 24;
  }


  onSpanClicked(e: {
    span: CalendarMonth.Span;
    segment: CalendarMonth.SpanSegmentInfo;
    info: CalendarMonthGesture.Info;
    $span: HTMLElement;
  }): void {
    const event = AllDaySpan.eventOf(e.span);
    if (!event) return;

    this.showExistingEventPopover(event, e.info.date, e.$span);
  }

  onSpanContextMenu(e: {
    span: CalendarMonth.Span;
    segment: CalendarMonth.SpanSegmentInfo;
    info: CalendarMonthGesture.Info;
    offset: Point;
    $span: HTMLElement;
  }): void {
    const event = AllDaySpan.eventOf(e.span);
    if (!event) return;

    // e.offset is cell-relative (converted from span-relative at the gesture layer).
    // e.info.date is the cell the pointer is in.
    const $cell = this.#ctx.$calendar.getCellForDate(e.info.date);
    if (!$cell) return;
    const calendarRect = this.#ctx.$calendar.getBoundingClientRect();
    const cellRect = $cell.getBoundingClientRect();
    // Spans sit below the day-number row within the cell.
    // Offset Y by the day-number row height so the menu appears at the span.
    const dayNumberRowHeight = this.#ctx.$calendar.getDayNumberRowHeight($cell);
    this.showContextMenu(
      event,
      {
        x: cellRect.left - calendarRect.left + e.offset.x,
        y: cellRect.top - calendarRect.top + dayNumberRowHeight + e.offset.y,
      },
      e.$span,
    );
  }


  async onEntryRowClicked(e: {
    eventData: ListItem;
    cell: CalendarMonthGesture.Info;
    $row: HTMLElement;
  }): Promise<void> {
    const eventId = EventId.parse(e.eventData.id ?? "");
    if (!eventId) return;

    const raw = this.#ctx.eventsSource.peek(eventId);
    const event = raw
      ? EventItem.of(raw.inner, raw.origin)
      : null;
    if (!event || !(event instanceof EventItemTimed)) return;

    // Position event details below the clicked row.
    // Compute calendar-relative Y so revealPopover can use it directly.
    const calendarRect = this.#ctx.$calendar.getBoundingClientRect();
    const rowRect = e.$row.getBoundingClientRect();
    const calendarRelativeY = rowRect.bottom - calendarRect.top;

    this.showExistingPartialDayEventPopover(
      event,
      e.cell.date,
      calendarRelativeY,
      e.$row,
    );
  }


  // ── Create-flow handlers + span builders (moved from
  // CalendarMonthHost in Phase F.5d). The layout's gesture
  // registration wires these into the `CalendarMonthNewGestures`
  // factory; `buildAllSpans` feeds the widget's span lane.

  onCreateStart(_date: NaiveDate): boolean {
    if (this.#ctx.delegate.getMonth() == null) return false;
    // A read-only list, or a create target that is not writable, takes no new event (N3-1).
    if (this.#ctx.eventsSource.createTarget() == null) return false;
    if (!this.#ctx.calendars.peekPrimaryOrFirst()) return false;

    // Dismiss context menu if open (same-system coordination)
    this.dismissContextMenu();

    this.#ctx.delegate.setPendingEvent({
      dateRange: new NaiveDate.Range(_date, _date),
      title: null,
      category: null,
      backgroundColor: null,
    });
    this.#ctx.delegate.rebindCalendar();
    return true;
  }

  onCreateDrag(dateRange: NaiveDate.Range): void {
    if (!this.#ctx.delegate.getPendingEvent()) return;
    this.#setPendingEventRange(dateRange);

    // Use updateSpans for efficient drag updates (bypasses cell re-rendering)
    this.#ctx.$calendar.updateSpans(this.#spans.buildAllSpans());
  }

  onCreateDragEnd(dateRange: NaiveDate.Range): void {
    if (!this.#ctx.delegate.getPendingEvent()) return;
    this.#setPendingEventRange(dateRange);

    // Show inline popover instead of modal
    this.showNewEventPopover(dateRange);
  }

  cancelPendingEvent(): void {
    this.#ctx.delegate.setPendingEvent(null);
    this.hidePopover();
  }

}
